import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { isNil, filter } from 'lodash-es';

/**
 * Modal-friendly version of useFeedPageFilters that uses react-hook-form
 * instead of URL query params. Designed for Card structured search modal.
 */
// Default Meeting content type sub_sources
const DEFAULT_MEETING_SUB_SOURCES = [
  'Google_Meeting',
  'Teams_Meeting',
  'Webex_Meeting',
  'Zoom_Meeting'
];

const DEFAULT_VALUES = {
  savedFilter: null,
  archivedFilter: null,
  sharedFilter: null,
  focuses: [],
  labels: [],
  dateRange: 'All Dates',
  keyword: '',
  contentTypeSubSources: DEFAULT_MEETING_SUB_SOURCES,
  createdAtFrom: null,
  createdAtTo: null,
  participantId: null,
  participantName: '',
  participantEmail: '',
  participantCompanyName: '',
  participantCompanyDomain: ''
};

export const useCardSearchFilters = () => {
  const methods = useForm({
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange'
  });

  const { watch, setValue, reset, getValues } = methods;

  // Watch all form values for computed properties
  const filters = watch();
  const {
    keyword,
    createdAtFrom,
    createdAtTo,
    focuses,
    labels,
    contentTypeSubSources,
    savedFilter,
    archivedFilter,
    sharedFilter,
    participantId,
    participantName,
    participantEmail,
    participantCompanyName,
    participantCompanyDomain
  } = filters;

  const handleFilterChange = useCallback(
    (filterName, value) => {
      setValue(filterName, value, { shouldDirty: true });
    },
    [setValue]
  );

  const handleResetFilters = useCallback(() => {
    reset(DEFAULT_VALUES);
  }, [reset]);

  const activeAttrFilterCount = useMemo(() => {
    const attrFilters = {
      keyword,
      createdAtFrom,
      createdAtTo
    };
    return filter(attrFilters, (f) => {
      if (typeof f === 'string') {
        return f?.length > 0;
      } else {
        return !isNil(f);
      }
    }).length;
  }, [keyword, createdAtFrom, createdAtTo]);

  const activeFocusFilterCount = focuses?.length;
  const activeLabelsFilterCount = labels?.length;
  const contentTypeSubSourcesCount = contentTypeSubSources?.length;

  const hasActiveFilters = useMemo(
    () =>
      savedFilter ||
      archivedFilter ||
      sharedFilter ||
      !!participantId ||
      !!participantName ||
      !!participantEmail ||
      !!participantCompanyName ||
      !!participantCompanyDomain ||
      activeFocusFilterCount > 0 ||
      activeAttrFilterCount > 0 ||
      activeLabelsFilterCount > 0 ||
      contentTypeSubSourcesCount > 0,
    [
      savedFilter,
      archivedFilter,
      sharedFilter,
      participantId,
      participantName,
      participantEmail,
      participantCompanyName,
      participantCompanyDomain,
      activeFocusFilterCount,
      activeAttrFilterCount,
      activeLabelsFilterCount,
      contentTypeSubSourcesCount
    ]
  );

  const activeFilterCount = useMemo(
    () => activeFocusFilterCount + activeAttrFilterCount,
    [activeFocusFilterCount, activeAttrFilterCount]
  );

  /**
   * Converts the filter state to API search body format for useCardsCleanBody
   */
  const getSearchBody = useCallback(() => {
    const values = getValues();
    const body = {
      initiative_type: 'Focus'
    };

    // Keyword search
    if (values.keyword && values.keyword.trim()) {
      body.keyword = values.keyword.trim();
    } else {
      body.sort = 'created_at';
      body.asc = false;
    }

    // Content type filters
    if (values.contentTypeSubSources && values.contentTypeSubSources.length > 0) {
      body.sub_sources = values.contentTypeSubSources;
    }

    // Focus/Category filters
    if (values.focuses && values.focuses.length > 0) {
      body.initiative_ids = values.focuses;
    }

    // Label filters
    if (values.labels && values.labels.length > 0) {
      body.label_ids = values.labels;
    }

    // Date range filters
    if (values.createdAtFrom) {
      body.created_at_from = values.createdAtFrom;
    }
    if (values.createdAtTo) {
      body.created_at_to = values.createdAtTo;
    }

    // Participant filters
    if (values.participantName && values.participantName.trim()) {
      body.participant_name = values.participantName.trim();
    }
    if (values.participantEmail && values.participantEmail.trim()) {
      body.participant_email = values.participantEmail.trim();
    }
    if (values.participantId) {
      body.participant_id = values.participantId;
    }

    // Company filters
    if (values.participantCompanyName && values.participantCompanyName.trim()) {
      body.participant_company_name = values.participantCompanyName.trim();
    }
    if (values.participantCompanyDomain && values.participantCompanyDomain.trim()) {
      body.participant_company_domain = values.participantCompanyDomain.trim();
    }

    // Saved/Archived filters
    if (values.savedFilter) {
      body.preferred = true;
    }
    if (values.archivedFilter) {
      body.archived = true;
    }
    if (values.sharedFilter) {
      body.shared = true;
    }

    return body;
  }, [getValues]);

  return {
    methods,
    filters,
    hasActiveFilters,
    activeFilterCount,
    handleFilterChange,
    handleResetFilters,
    getSearchBody,
    // Additional state setters for direct manipulation if needed
    setStartDate: (value) => setValue('createdAtFrom', value),
    setEndDate: (value) => setValue('createdAtTo', value)
  };
};
